import * as Model from "@effect/sql/Model";
import { Schema } from "effect";

/**
 * Connection types for installation integrations.
 * @since 1.0.0
 */
export const ConnectionType = Schema.Literal("channel", "file-provider", "connector", "email");
export type ConnectionType = Schema.Schema.Type<typeof ConnectionType>;

/**
 * Connection status for installation integrations.
 * @since 1.0.0
 */
export const ConnectionStatus = Schema.Literal("pending", "connected", "failed", "revoked");
export type ConnectionStatus = Schema.Schema.Type<typeof ConnectionStatus>;

/**
 * How much of Google Drive the customer granted (CLA drive-access mode):
 * `"picked"` = the non-sensitive `drive.file` scope over items the customer
 * chose in the Google Picker; `"full"` = the restricted `…/auth/drive` scope
 * that browses the whole Drive.
 *
 * @since 1.0.0
 */
export const GoogleDriveAccess = Schema.Literal("picked", "full");
export type GoogleDriveAccess = Schema.Schema.Type<typeof GoogleDriveAccess>;

/**
 * One item chosen in the Google Picker (a `docs[]` entry). `kind` is `"folder"`
 * when `mimeType === "application/vnd.google-apps.folder"`, else `"file"`.
 *
 * @since 1.0.0
 */
export const GoogleDrivePickedItem = Schema.Struct({
  id: Schema.NonEmptyTrimmedString,
  name: Schema.String,
  mimeType: Schema.String,
  kind: Schema.Literal("file", "folder"),
  parentId: Schema.optional(Schema.String),
  url: Schema.optional(Schema.String),
});
export type GoogleDrivePickedItem = Schema.Schema.Type<typeof GoogleDrivePickedItem>;

/**
 * Typed view of `installation_connections.metadata` for `provider = "google"`.
 * `hd` is the Workspace hosted-domain claim (present only for Workspace
 * accounts); `drive` records the chosen access mode and, for `"picked"`, the
 * Picker selection captured by the `connections/googleDriveSelection` RPC.
 *
 * @since 1.0.0
 */
export const GoogleConnectionMetadata = Schema.Struct({
  hd: Schema.optional(Schema.String),
  drive: Schema.optional(Schema.Struct({
    access: GoogleDriveAccess,
    items: Schema.optional(Schema.Array(GoogleDrivePickedItem)),
    pickedAt: Schema.optional(Schema.DateTimeUtc),
  })),
});
export type GoogleConnectionMetadata = Schema.Schema.Type<typeof GoogleConnectionMetadata>;

/**
 * InstallationConnection model representing an OAuth/integration connection
 * scoped to a single installation.
 *
 * Replaces the onboarding-keyed `OnboardingConnection` from the pre-cutover
 * schema. `attribution` carries free-form identity of the last modifier so
 * audit log readers don't have to reverse-resolve it from sibling tables.
 *
 * @since 1.0.0
 */
export class InstallationConnection extends Model.Class<InstallationConnection>("InstallationConnection")({
  id: Model.Generated(Schema.String),

  installationId: Schema.String,

  provider: Schema.NonEmptyTrimmedString,
  accountKey: Schema.String,
  type: ConnectionType,
  status: ConnectionStatus,

  accessToken: Schema.NullOr(Schema.String),
  refreshToken: Schema.NullOr(Schema.String),
  // Encrypted-at-rest ciphertext (base64 AES-GCM envelope over
  // `JSON.stringify(fieldName→value)`) for providers that need more than the
  // two legacy token slots. Typed as the ciphertext `string` here — exactly
  // like `accessToken`/`refreshToken` — because the row carries the encrypted
  // value; decrypt + JSON-parse into the `fields` map happens at the service
  // layer (mgmt.fetchConnectionSecret). NULL for every existing row.
  secretFields: Schema.NullOr(Schema.String),
  tokenExpiresAt: Schema.NullOr(Schema.DateTimeUtc),
  scopes: Schema.NullOr(Schema.Array(Schema.String)),

  accountLabel: Schema.NullOr(Schema.String),
  metadata: Schema.NullOr(Schema.Unknown),

  attribution: Schema.NullOr(Schema.String),

  connectedAt: Schema.NullOr(Schema.DateTimeUtc),
  createdAt: Model.DateTimeInsertFromDate,
  updatedAt: Model.DateTimeUpdateFromDate,
}) {}
